import { useState } from 'react'
import { type TextOptions } from '@backend/lib/db/typeRoots/layoutNode'
import { colorToHex, colorToString } from '@/components/modules/CanvasEditor/color'
import {
  AlignLeftIcon,
  AlignTextCenterIcon,
  AlignTextRightIcon,
  BottomAlignIcon,
  BringForwardIcon,
  CenterAlignIcon,
  Delete1Icon,
  LayersIcon,
  LineSpaceIcon,
  OutlineIcon,
  SendBackwardIcon,
  SendToBackIcon,
  SendToFrontIcon,
  ShadowIcon,
  TopAlignIcon,
} from '@/components/ui/icons/icons-new'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/shadcn/tooltip'
import { PopoverMenu } from './common/popover-menu'
import { ToggleMenu } from '@/components/ui/ToggleMenu'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/shadcn/dropdown-menu'
import { ColorPickerQuickmenu } from '@/components/ui/ColorPicker'
import { type Font } from '@/hooks/editor/useFontLoader'
import { WorkspaceEditorMenuWrapper } from './workspace-editor-menu-wrapper'
import { LockToggleButton } from './common/lock-toggle-button'
import { BorderIcon } from '@/components/ui/icons/icons-new'
import { TextBgColorIcon } from '@/components/ui/icons/TextBgColorIcon'
import { SeparatorQuickmenu } from '@/components/ui/SeparatorQuickmenu'
import { NumberInput } from './common/number-input'
import { useTheme } from '@/context/themeContext'
import { cn } from '@/lib/utils'
import { FillPicker } from './common/fill-picker'

interface Props {
  fonts: Font[]
  // Fonts uploaded to the workspace's moodboard. Shown in a group above `fonts`.
  customFonts?: Font[]
  options: TextOptions
  onUpdate: (updates: Partial<TextOptions>, isHoverSelection?: boolean) => void
  onLayerChange: (operation: 'bringToFront' | 'moveForward' | 'moveBackward' | 'sendToBack') => void
  onDelete: () => void
  onDuplicate: () => void
  locked: boolean
  onToggleLock: () => void
  colorPalette: string[]
}

export function WorkspaceEditorMenuText({
  fonts,
  customFonts = [],
  options,
  onUpdate,
  onLayerChange,
  onDelete,
  onDuplicate,
  locked,
  onToggleLock,
  colorPalette,
}: Props) {
  const { theme } = useTheme()
  const [initialFont, setInitialFont] = useState(options.fontFamily)
  const [initialWeight, setInitialWeight] = useState(options.fontWeight)
  const [initialStyle, setInitialStyle] = useState(options.fontStyle)

  // Custom fonts are also registered in document.fonts, so useFontsLoader
  // enumerates them into `fonts` too — drop them from the default list so they
  // only appear once, in the "Custom fonts" group.
  const customFamilies = new Set(customFonts.map((f) => f.fontFamily))
  const defaultFonts = fonts.filter((f) => !customFamilies.has(f.fontFamily))

  return (
    <WorkspaceEditorMenuWrapper>
      <TooltipProvider delayDuration={300}>
        {/* fontColor */}
        <PopoverMenu
          trigger={
            <div
              className="border-border h-7 w-7 rounded-full border"
              style={{ backgroundColor: colorToHex(options.fontColor) }}
            />
          }
          hoverContent="Font color"
          options={[
            {
              label: 'font color',
              content: (
                <ColorPickerQuickmenu
                  value={options.fontColor}
                  onChange={(selectedKey, isHoverSelection) => {
                    onUpdate({ fontColor: selectedKey }, isHoverSelection)
                  }}
                  colorPalette={colorPalette}
                />
              ),
            },
          ]}
        />
        {/* stroke*/}
        <PopoverMenu
          trigger={
            <OutlineIcon width={24} height={24} color={colorToString(options.strokeColor, 'hex')} />
          }
          hoverContent="Font border color, size"
          options={[
            {
              label: 'stroke width',
              content: (
                <NumberInput
                  value={options.strokeWidth}
                  onChange={(selectedKey, isHoverSelection) =>
                    onUpdate({ strokeWidth: selectedKey }, isHoverSelection)
                  }
                  onBlur={(selectedKey) => onUpdate({ strokeWidth: selectedKey })}
                  min={0}
                  max={9999}
                  step={1}
                />
              ),
            },
            {
              label: 'stroke color',
              content: (
                <ColorPickerQuickmenu
                  value={options.strokeColor}
                  onChange={(selectedKey, isHoverSelection) => {
                    onUpdate({ strokeColor: selectedKey }, isHoverSelection)
                  }}
                  colorPalette={colorPalette}
                />
              ),
            },
          ]}
        />
        {/* fontFamily */}

        <DropdownMenu
          modal={false}
          dir="ltr"
          onOpenChange={(open) => {
            if (open) {
              setTimeout(() => {
                const activeItem = document.querySelector('[data-active="true"]')
                if (activeItem) {
                  activeItem.scrollIntoView({ block: 'center' })
                }
              }, 0)
            }
          }}>
          <Tooltip>
            <TooltipTrigger>
              <DropdownMenuTrigger
                asChild
                className="text-foreground border-border hover:bg-hover data-[state=open]:bg-hover flex h-7 cursor-pointer items-center justify-center overflow-hidden text-ellipsis whitespace-nowrap rounded-lg border px-3 text-sm"
                style={{ fontFamily: initialFont }}>
                <span>{initialFont}</span>
              </DropdownMenuTrigger>
            </TooltipTrigger>
            <TooltipContent>
              <p>Font family</p>
            </TooltipContent>
          </Tooltip>

          <DropdownMenuContent
            className={cn(
              'border-border shadow-xs h-[350px] space-y-1 border p-2',
              theme === 'dark' ? 'dark' : '',
            )}
            side="top"
            align="start"
            sideOffset={16}
            onFocusOutside={(e) => {
              // Same dismissal-on-focus-outside issue as the toolbar's
              // PopoverContent — when applied to a Lexical text selection,
              // the editor's reconciliation moves focus to body and Radix
              // closes the menu mid-pick. Pointer-down-outside still
              // closes the menu on genuine outside clicks.
              e.preventDefault()
            }}>
            {customFonts.length > 0 && (
              <>
                <DropdownMenuLabel className="text-muted-foreground px-2 py-1 text-xs font-medium">
                  Custom fonts
                </DropdownMenuLabel>
                {customFonts.map((font) => (
                  <FontFamilyItem
                    key={`custom-${font.fontFamily}`}
                    font={font}
                    isActive={font.fontFamily === initialFont}
                    initialFont={initialFont}
                    initialWeight={initialWeight}
                    initialStyle={initialStyle}
                    setInitialFont={setInitialFont}
                    setInitialWeight={setInitialWeight}
                    setInitialStyle={setInitialStyle}
                    onUpdate={onUpdate}
                  />
                ))}
                <DropdownMenuSeparator className="bg-border my-1" />
              </>
            )}
            {defaultFonts.map((font) => (
              <FontFamilyItem
                key={font.fontFamily}
                font={font}
                isActive={font.fontFamily === initialFont}
                initialFont={initialFont}
                initialWeight={initialWeight}
                initialStyle={initialStyle}
                setInitialFont={setInitialFont}
                setInitialWeight={setInitialWeight}
                setInitialStyle={setInitialStyle}
                onUpdate={onUpdate}
              />
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
        {/* fontWeight, fontStyle */}
        <DropdownMenu
          modal={false}
          onOpenChange={(open) => {
            if (open) {
              setTimeout(() => {
                const activeItem = document.querySelector('[data-active="true"]')
                if (activeItem) {
                  activeItem.scrollIntoView({ block: 'center' })
                }
              }, 0)
            }
          }}>
          <Tooltip>
            <TooltipTrigger>
              <DropdownMenuTrigger
                asChild
                className="text-foreground border-border hover:bg-hover data-[state=open]:bg-hover flex h-7 cursor-pointer items-center justify-center overflow-hidden text-ellipsis whitespace-nowrap rounded-lg border px-3 text-sm">
                <span
                  style={{
                    fontWeight: initialWeight,
                    fontStyle: initialStyle,
                  }}>{`${getFontWeightLabel(initialWeight)}${initialStyle === 'italic' ? ' Italic' : ''}`}</span>
              </DropdownMenuTrigger>
            </TooltipTrigger>
            <TooltipContent>
              <p>Font weight, style</p>
            </TooltipContent>
          </Tooltip>
          <DropdownMenuContent
            className={cn(
              'border-border shadow-xs space-y-1 border p-2',
              theme === 'dark' ? 'dark' : '',
            )}
            side="top"
            align="start"
            sideOffset={10}
            onFocusOutside={(e) => e.preventDefault()}>
            {[...customFonts, ...fonts]
              .find((font) => font.fontFamily === options.fontFamily)
              ?.weights.sort((a, b) =>
                a.style === b.style ? a.value - b.value : a.style === 'normal' ? -1 : 1,
              )
              .map((weight) => (
                <DropdownMenuItem
                  className="text-foreground font-regular focus:bg-hover text-sm"
                  key={`${weight.value}${weight.style}`}
                  data-active={weight.value === initialWeight && weight.style === initialStyle}
                  onClick={(e) => {
                    e.stopPropagation()
                    setInitialWeight(weight.value)
                    setInitialStyle(weight.style)
                    onUpdate({
                      fontStyle: weight.style,
                      fontWeight: weight.value,
                    })
                  }}
                  onMouseEnter={(e) => {
                    e.stopPropagation()
                    onUpdate(
                      {
                        fontStyle: weight.style,
                        fontWeight: weight.value,
                      },
                      true,
                    )
                  }}
                  onMouseLeave={(e) => {
                    e.stopPropagation()
                    onUpdate(
                      {
                        fontStyle: initialStyle,
                        fontWeight: initialWeight,
                      },
                      true,
                    )
                  }}
                  style={{
                    fontWeight: weight.value,
                    fontStyle: weight.style,
                  }}>
                  {`${getFontWeightLabel(weight.value)}${weight.style === 'italic' ? ' Italic' : ''}`}
                </DropdownMenuItem>
              ))}
          </DropdownMenuContent>
        </DropdownMenu>
        {/* fontSize */}
        <NumberInput
          //  NOTE: using key to force component remount when change selected text node
          key={options.fontSize}
          value={options.fontSize}
          onChange={(selectedKey, isHoverSelection) =>
            onUpdate({ fontSize: selectedKey }, isHoverSelection)
          }
          onBlur={(selectedKey) => onUpdate({ fontSize: selectedKey })}
          min={1}
          max={9999}
          step={1}
          hoverContent="Font size"
        />

        {/* backdrop - background color*/}
        <PopoverMenu
          trigger={<TextBgColorIcon secondaryColor={colorToString(options.backdropColor, 'hex')} />}
          hoverContent="Background color, padding"
          options={[
            {
              label: 'background padding',
              content: (
                <NumberInput
                  value={options.backdropPadding}
                  onChange={(selectedKey, isHoverSelection) =>
                    onUpdate({ backdropPadding: selectedKey }, isHoverSelection)
                  }
                  onBlur={(selectedKey) => onUpdate({ backdropPadding: selectedKey })}
                  min={0}
                  max={9999}
                  step={1}
                />
              ),
            },
            {
              label: 'background color',
              content: (
                <FillPicker
                  solidColor={options.backdropColor}
                  gradient={options.backdropGradient}
                  onSolidChange={(color, isHover) =>
                    onUpdate({ backdropColor: color, backdropGradient: undefined }, isHover)
                  }
                  onGradientChange={(gradient, isHover) =>
                    onUpdate({ backdropGradient: gradient }, isHover)
                  }
                  colorPalette={colorPalette}
                />
              ),
            },
          ]}
        />
        {/* horizontal align */}
        <PopoverMenu
          trigger={
            options.horizontalAlign === 'left' ? (
              <AlignLeftIcon width={22} height={22} color="var(--foreground)" />
            ) : options.horizontalAlign === 'center' ? (
              <AlignTextCenterIcon width={22} height={22} color="var(--foreground)" />
            ) : (
              <AlignTextRightIcon width={22} height={22} color="var(--foreground)" />
            )
          }
          hoverContent="Text formatting"
          options={[
            {
              label: 'horizontal align',
              content: (
                <ToggleMenu
                  type="single"
                  selectedKey={options.horizontalAlign}
                  options={[
                    {
                      key: 'left',
                      label: <AlignLeftIcon width={22} height={22} color="var(--foreground)" />,
                    },
                    {
                      key: 'center',
                      label: (
                        <AlignTextCenterIcon width={22} height={22} color="var(--foreground)" />
                      ),
                    },
                    {
                      key: 'right',
                      label: (
                        <AlignTextRightIcon width={22} height={22} color="var(--foreground)" />
                      ),
                    },
                  ]}
                  onClick={(selectedKey) =>
                    onUpdate({ horizontalAlign: selectedKey as 'left' | 'center' | 'right' })
                  }
                />
              ),
            },
          ]}
        />
        {/* backdrop - background padding, radius*/}
        <PopoverMenu
          trigger={<BorderIcon width={22} height={22} color="var(--foreground)" />}
          hoverContent="Background border color, rounded corners"
          options={[
            {
              label: 'rounded corners',
              content: (
                <NumberInput
                  value={options.backdropBorderRadius}
                  onChange={(selectedKey, isHoverSelection) =>
                    onUpdate({ backdropBorderRadius: selectedKey }, isHoverSelection)
                  }
                  onBlur={(selectedKey) => onUpdate({ backdropBorderRadius: selectedKey })}
                  min={0}
                  max={9999}
                  step={1}
                />
              ),
            },
            {
              label: 'background border width',
              content: (
                <NumberInput
                  value={options.backdropBorderWidth}
                  onChange={(selectedKey, isHoverSelection) =>
                    onUpdate({ backdropBorderWidth: selectedKey }, isHoverSelection)
                  }
                  onBlur={(selectedKey) => onUpdate({ backdropBorderWidth: selectedKey })}
                  min={0}
                  max={9999}
                  step={1}
                />
              ),
            },
            {
              label: 'background border color',
              content: (
                <ColorPickerQuickmenu
                  value={options.backdropBorderColor}
                  onChange={(selectedKey, isHoverSelection) => {
                    onUpdate({ backdropBorderColor: selectedKey }, isHoverSelection)
                  }}
                  colorPalette={colorPalette}
                />
              ),
            },
          ]}
        />

        {/* line height */}
        <PopoverMenu
          trigger={<LineSpaceIcon width={22} height={22} color="var(--foreground)" />}
          hoverContent="Text spacing"
          options={[
            {
              label: 'line height',
              content: (
                <NumberInput
                  value={options.lineHeight}
                  onChange={(selectedKey, isHoverSelection) =>
                    onUpdate({ lineHeight: selectedKey }, isHoverSelection)
                  }
                  onBlur={(selectedKey) => onUpdate({ lineHeight: selectedKey })}
                  min={0}
                  max={999}
                  step={0.01}
                />
              ),
            },
          ]}
        />
        {/* vertical align */}
        <PopoverMenu
          trigger={
            options.verticalAlign === 'top' ? (
              <TopAlignIcon color="var(--foreground)" />
            ) : options.verticalAlign === 'center' ? (
              <CenterAlignIcon color="var(--foreground)" />
            ) : (
              <BottomAlignIcon color="var(--foreground)" />
            )
          }
          hoverContent="Text alignment"
          options={[
            {
              label: 'vertical align',
              content: (
                <ToggleMenu
                  type="single"
                  selectedKey={options.verticalAlign}
                  options={[
                    {
                      key: 'top',
                      label: <TopAlignIcon color="var(--foreground)" />,
                    },
                    {
                      key: 'center',
                      label: <CenterAlignIcon color="var(--foreground)" />,
                    },
                    {
                      key: 'bottom',
                      label: <BottomAlignIcon color="var(--foreground)" />,
                    },
                  ]}
                  onClick={(selectedKey) =>
                    onUpdate({ verticalAlign: selectedKey as 'top' | 'center' | 'bottom' })
                  }
                />
              ),
            },
          ]}
        />

        <SeparatorQuickmenu />

        {/* Layers */}
        <PopoverMenu
          trigger={<LayersIcon width={22} height={22} color="var(--foreground)" />}
          hoverContent="Layers"
          options={[
            {
              label: '',
              content: (
                <span
                  className="hover:bg-hover font-regular flex cursor-pointer items-center justify-start gap-2 rounded-sm p-1 text-sm"
                  onClick={() => onLayerChange('sendToBack')}>
                  <SendToBackIcon width={22} height={22} color="var(--foreground)" />
                  Send to back
                </span>
              ),
            },
            {
              label: '',
              content: (
                <span
                  className="hover:bg-hover font-regular flex cursor-pointer items-center justify-start gap-2 rounded-sm p-1 text-sm"
                  onClick={() => onLayerChange('moveBackward')}>
                  <SendBackwardIcon width={22} height={22} color="var(--foreground)" />
                  Send backward
                </span>
              ),
            },
            {
              label: '',
              content: (
                <span
                  className="hover:bg-hover font-regular flex cursor-pointer items-center justify-start gap-2 rounded-sm p-1 text-sm"
                  onClick={() => onLayerChange('moveForward')}>
                  <BringForwardIcon width={22} height={22} color="var(--foreground)" />
                  Bring forward
                </span>
              ),
            },
            {
              label: '',
              content: (
                <span
                  className="hover:bg-hover font-regular flex cursor-pointer items-center justify-start gap-2 rounded-sm p-1 text-sm"
                  onClick={() => onLayerChange('bringToFront')}>
                  <SendToFrontIcon width={22} height={22} color="var(--foreground)" />
                  Bring to front
                </span>
              ),
            },
          ]}
        />
        <Tooltip>
          <TooltipTrigger>
            <span
              className="hover:bg-hover flex cursor-pointer items-center justify-center rounded-sm p-1"
              onClick={onDuplicate}>
              <ShadowIcon width={22} height={22} color="var(--foreground)" />
            </span>
          </TooltipTrigger>
          <TooltipContent className="w-full">
            <p>Duplicate item</p>
          </TooltipContent>
        </Tooltip>
        <LockToggleButton locked={locked} onToggle={onToggleLock} />
        <Tooltip>
          <TooltipTrigger>
            <span
              onClick={onDelete}
              className="hover:bg-hover flex cursor-pointer items-center justify-center rounded-sm p-1">
              <Delete1Icon width={22} height={22} color="var(--foreground)" />
            </span>
          </TooltipTrigger>
          <TooltipContent className="w-full">
            <p>Delete item</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </WorkspaceEditorMenuWrapper>
  )
}

// One row in the font-family dropdown. Shared by the default and custom-font
// groups so their pick / hover-preview / restore behaviour stays identical.
function FontFamilyItem({
  font,
  isActive,
  initialFont,
  initialWeight,
  initialStyle,
  setInitialFont,
  setInitialWeight,
  setInitialStyle,
  onUpdate,
}: {
  font: Font
  isActive: boolean
  initialFont: string
  initialWeight: number
  initialStyle: 'normal' | 'italic'
  setInitialFont: (value: string) => void
  setInitialWeight: (value: number) => void
  setInitialStyle: (value: 'normal' | 'italic') => void
  onUpdate: (updates: Partial<TextOptions>, isHoverSelection?: boolean) => void
}) {
  // Keep the current weight/style if the family offers it, else fall back to
  // Regular, else its first available variant.
  const resolveWeight = () =>
    font.weights.find((w) => w.value === initialWeight && w.style === initialStyle) ??
    font.weights.find((w) => w.value === 400 && w.style === 'normal') ??
    font.weights[0] ?? { value: 400, style: 'normal' as const }

  return (
    <DropdownMenuItem
      style={{ fontFamily: font.fontFamily }}
      className="text-foreground font-regular focus:bg-hover text-sm"
      data-active={isActive}
      onClick={() => {
        const weight = resolveWeight()
        setInitialFont(font.fontFamily)
        setInitialStyle(weight.style)
        setInitialWeight(weight.value)
        onUpdate({ fontFamily: font.fontFamily, fontWeight: weight.value, fontStyle: weight.style })
      }}
      onMouseEnter={() => {
        const weight = resolveWeight()
        onUpdate(
          { fontFamily: font.fontFamily, fontWeight: weight.value, fontStyle: weight.style },
          true,
        )
      }}
      onMouseLeave={() =>
        onUpdate(
          { fontFamily: initialFont, fontWeight: initialWeight, fontStyle: initialStyle },
          true,
        )
      }>
      {font.fontFamily}
    </DropdownMenuItem>
  )
}

const getFontWeightLabel = (weight: number | undefined) => {
  if (weight === undefined) return 'Regular'

  const weightLabels: Record<number, string> = {
    100: 'Thin',
    200: 'Extra Light',
    300: 'Light',
    400: 'Regular',
    500: 'Medium',
    600: 'Semi Bold',
    700: 'Bold',
    800: 'Extra Bold',
    900: 'Black',
  }
  return weightLabels[weight] || weight.toString()
}
